<template>
	<div>
		<div
			class="modal fade modal-fullscreen-mobile"
			ref="modal"
			tabindex="-1"
			role="dialog"
			aria-hidden="true"
			data-focus="false"
		>
			<div class="modal-dialog vld-parent" role="document">
				<div class="modal-content">
					<div class="modal-body">
						<loading :active="loading" />
						<button
							type="button"
							class="modal-close"
							@click="close"
							aria-label="Close"
						>
							<i class="sk-icon-times"></i>
						</button>
						<div class="modal-inner-content">
							<h1 class="modal-title">{{ title }}</h1>
							<h2 class="modal-subtitle">
								{{ subtitle }}
							</h2>
							<div class="step-container">
								<div
									class="step"
									v-for="(step, index) in steps"
									:key="index"
								>
									<img
										:src="step.imageUrl"
										:alt="`Step ${index}`"
									/>
									<div>
										<div class="step-title">
											{{ step.title }}
										</div>
										<div class="step-description">
											{{ step.description }}
										</div>
									</div>
								</div>
							</div>
							<button
								class="button button-primary"
								@click="clickCta"
							>
								{{ ctaText }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
		<DeliveryTimeModal ref="timeModal" v-if="type == 3" />
	</div>
</template>

<style scoped>
.modal-body {
	justify-content: flex-start;
}

.modal-title {
	font-size: 24px;
	margin-top: 25px;
	text-align: center;
}

.modal-subtitle {
	font-size: 14px;
	color: var(--sk-grey3);
	text-align: center;
	margin-bottom: 0;
}

.modal-inner-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	height: 100%;
}

.modal-inner-content .button {
	flex-shrink: 0;
}

.step-container {
	margin: 60px 0;
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: center;
}

.step {
	display: flex;
	align-items: center;
}

.step + .step {
	margin-top: 60px;
}

.step > img {
	width: 75px;
	height: 75px;
	margin-right: 20px;
}

.step > div {
	max-width: 220px;
}

.step-title {
	font-size: 14px;
	font-weight: 600;
	color: var(--gR-base-90);
}

.step-description {
	font-size: 12px;
	color: var(--sk-grey3);
}

@media only screen and (max-width: 400px) {
	.step-container {
		margin: 0;
	}

	.modal-title {
		margin-top: 0;
	}
}
</style>

<script>
import { ExperimentType } from "@/enums/ExperimentType"
import { eventBus, EventBusEvents } from "@/eventBus"
import { CouponType } from "@reef-os/utils/enums/couponType"
import moment from "moment"
import momentUtils from "@/moment-utils"
import Loading from "vue-loading-overlay"
import DeliveryTimeModal from "@/components/modals/DeliveryTimeModal.vue"

export default {
	name: "StepModal",
	components: {
		Loading,
		DeliveryTimeModal
	},
	data: function () {
		return {
			type: null,
			params: {}
		}
	},
	computed: {
		loading() {
			return (
				this.title.length <= 0 ||
				this.subtitle.length <= 0 ||
				this.steps.length <= 0 ||
				this.ctaText.length <= 0
			)
		},
		title() {
			switch (this.type) {
				case ExperimentType.REFERRAL_SMS: {
					return "Share & Earn"
				}
				case ExperimentType.PREORDER: {
					return "Order Ahead"
				}
				case ExperimentType.HAPPY_HOUR: {
					const businessName = this.$store.state.business
						? this.$store.state.business.name
						: ""
					return `Happy hour at ${businessName}`
				}
				case ExperimentType.ON_PREM: {
					return this.event != null ? this.event.name : ""
				}
			}
			return ""
		},
		restName() {
			return this.event != null
				? this.$store.state.restaurants[this.event.restaurant_id].name
				: ""
		},
		eventWeekday() {
			return this.event != null
				? moment(this.event.end_date, "YYYY-MM-DD").format("dddd")
				: ""
		},
		deliveryTime() {
			return this.event != null
				? moment(this.event.end_time, "HH:mm:ss").format("h:mm a")
				: ""
		},
		cutoffDay() {
			if (this.event != null) {
				return moment(
					this.event.pre_order_end_date,
					"YYYY-MM-DD"
				).format("dddd")
			}
			return ""
		},
		cutoffDate() {
			if (this.event != null) {
				return moment(
					this.event.pre_order_end_date,
					"YYYY-MM-DD"
				).format("MMMM Do")
			}
			return ""
		},
		cutoffTime() {
			if (this.event) {
				return moment(this.event.pre_order_end_time, "HH:mm:ss").format(
					"h:mm a"
				)
			}
			return ""
		},
		subtitle() {
			const businessName = this.$store.state.business
				? this.$store.state.business.name
				: ""
			switch (this.type) {
				case ExperimentType.REFERRAL_SMS: {
					return `Share the love with your friends & neighbors at ${businessName}!`
				}
				case ExperimentType.PREORDER: {
					return "Spend more time socializing and less time waiting for your meal by placing your order in advance!"
				}
				case ExperimentType.ON_PREM: {
					if (
						businessName != "" &&
						this.restName != "" &&
						this.eventWeekday != "" &&
						this.deliveryTime != ""
					) {
						return `${this.restName} is stopping by ${businessName} for an on site exclusive event on ${this.eventWeekday} at ${this.deliveryTime}. Pre-order your meal to reserve your spot! `
					}
					break
				}
				case ExperimentType.HAPPY_HOUR: {
					if (this.happyHourSchedule) {
						const days = {
							0: "Monday",
							1: "Tuesday",
							2: "Wednesday",
							3: "Thursday",
							4: "Friday",
							5: "Saturday",
							6: "Sunday"
						}
						let openSchedule = this.happyHourSchedule.openSchedule
						let closeSchedule = this.happyHourSchedule.closeSchedule

						if (openSchedule && closeSchedule) {
							let openDay = days[openSchedule[0].dow]
							let closeDay = days[closeSchedule[0].dow]
							let openTime = null
							let openTimeFormatted = ""
							let closeTime = null
							let closeTimeFormatted = ""
							if (openSchedule.length > 1) {
								let numOpenTime = 0
								openSchedule.forEach((schedule) => {
									const scheduleOpenTime =
										momentUtils.numericalTime(
											schedule.openTime
										)
									if (
										openTime == null ||
										scheduleOpenTime > numOpenTime
									) {
										numOpenTime = momentUtils.numericalTime(
											schedule.openTime
										)
										openTime = schedule.openTime
									}
								})
							} else {
								openTime = openSchedule[0].openTime
							}
							openTimeFormatted = moment(
								openTime,
								"HH:mm"
							).format("h:mm a")

							if (closeSchedule.length > 1) {
								let numCloseTime = 0
								closeSchedule.forEach((schedule) => {
									const scheduleCloseTime =
										momentUtils.numericalTime(
											schedule.closeTime
										)
									if (
										closeTime == null ||
										scheduleCloseTime < numCloseTime
									) {
										numCloseTime =
											momentUtils.numericalTime(
												schedule.closeTime
											)
										closeTime = schedule.closeTime
									}
								})
							} else {
								closeTime = closeSchedule[0].closeTime
							}
							closeTimeFormatted = moment(
								closeTime,
								"HH:mm"
							).format("h:mm a")
							return `Discounted menu every ${openDay} through ${closeDay} from ${openTimeFormatted}-${closeTimeFormatted}!`
						}
					}
					break
				}
			}
			return ""
		},
		steps() {
			switch (this.type) {
				case ExperimentType.REFERRAL_SMS: {
					return [
						{
							title: "Share Your Code",
							description:
								"Invite neighbors to order meals through 2ndKitchen using your unique code.",
							imageUrl: "/images/2k-referral.png"
						},
						{
							title: "Give $5",
							description:
								"Anyone who enters your code at checkout will recieve $5 off their meal.",
							imageUrl: "/images/2k-loyalty.png"
						},
						{
							title: "Get $10",
							description:
								"Earn $10 each time your referral code gets used. The more you give the more you get!",
							imageUrl: "/images/2k-coupon.png"
						}
					]
				}
				case ExperimentType.PREORDER: {
					return [
						{
							title: "1 Select Your Reservation",
							description:
								"Simply choose the date and time of your reservation to view the menu",
							imageUrl: "/images/2k-calendar.png"
						},
						{
							title: "2 Order & Pay",
							description:
								"Choose from delicious food options and utilize contactless online payment",
							imageUrl: "/images/2k-card-pay.png"
						},
						{
							title: "3 Track Your Order",
							description:
								"Get updates from the time your food is being prepared until it reaches you",
							imageUrl: "/images/2k-out-of-stock.png"
						}
					]
				}
				case ExperimentType.ON_PREM: {
					const businessName = this.$store.state.business
						? this.$store.state.business.name
						: ""
					let preorderDescription = ""
					if (
						this.event != null &&
						this.event.end_date == this.event.pre_order_end_date &&
						this.event.pre_order_end_time == "00:00:00"
					) {
						preorderDescription =
							"We recommend pre-ordering due to high demand. You can preorder until the day of the event"
					} else {
						preorderDescription = `We recommend pre-ordering due to high demand. You can pre order until ${this.cutoffDay} ${this.cutoffDate} at ${this.cutoffTime}`
					}
					if (
						preorderDescription != "" &&
						businessName != "" &&
						this.restName != "" &&
						this.eventWeekday != "" &&
						this.deliveryTime != ""
					) {
						return [
							{
								title: "1 Save the Date",
								description: `${this.restName} will be onsite at at ${businessName} on ${this.eventWeekday} at ${this.deliveryTime}`,
								imageUrl: "/images/2k-calendar.png"
							},
							{
								title: "2 Pre-Orders Recommended",
								description: preorderDescription,
								imageUrl: "/images/2k-card-pay.png"
							},
							{
								title: "3 Enjoy",
								description: `We will notify you when the food truck arrives. Just show your order receipt and grab your food!`,
								imageUrl: "/images/2k-out-of-stock.png"
							}
						]
					}
					break
				}
				case ExperimentType.HAPPY_HOUR: {
					const businessName = this.$store.state.business
						? this.$store.state.business.name
						: ""
					return [
						{
							title: "Explore Happy Hour",
							description: `Browse a special discounted Happy Hour menu found exclusively at ${businessName}`,
							imageUrl: "/images/2k-coupon.png"
						},
						{
							title: "Limited Time Deals",
							description:
								"Choose from delicious options, at exclusive prices, for a limited time only!",
							imageUrl: "/images/2k-out-of-stock.png"
						},
						{
							title: "Easily Order & Enjoy",
							description:
								"Automatically receive discounted Happy Hour pricing and free delivery to the lobby",
							imageUrl: "/images/2k-pickup.png"
						}
					]
				}
			}
			return []
		},
		ctaText() {
			switch (this.type) {
				case ExperimentType.REFERRAL_SMS: {
					const coupon = this.$store.state.user.coupons.find(
						(coupon) => coupon.coupon_type == CouponType.REFERRAL
					)
					const code =
						coupon && coupon.code
							? `: ${coupon.code.toUpperCase()}`
							: ""
					return `Share Your Referral Code${code}`
				}
				case ExperimentType.PREORDER: {
					return "Start Pre Order"
				}
				case ExperimentType.HAPPY_HOUR: {
					return "Explore Happy Hour Menu"
				}
				case ExperimentType.ON_PREM: {
					return "Pre Order Now"
				}
			}
			return ""
		},
		happyHourAvailableDays() {
			let menuSchedule =
				this.params && this.params.menuId
					? this.$store.state.menuSchedules[this.params.menuId]
					: null
			if (menuSchedule) {
				return Object.values(menuSchedule)
					.filter((schedule) => schedule.length > 0)
					.map((schedule) => schedule[0].dow)
			}
			return null
		},
		event() {
			let businessEvent =
				this.params &&
				this.params.eventId &&
				this.$store.state.businessEvents
					? this.$store.state.businessEvents.find(
							(menu) => menu.id == this.params.eventId
					  )
					: null
			return businessEvent
		},
		happyHourSchedule() {
			let menuSchedule =
				this.params && this.params.menuId
					? this.$store.state.menuSchedules[this.params.menuId]
					: null
			if (menuSchedule) {
				let openSchedule = null
				let closeSchedule = null
				let availableDows = this.happyHourAvailableDays
				if (availableDows.length == 7) {
					openSchedule = menuSchedule[0]
					closeSchedule = menuSchedule[6]
				} else {
					let rollsOver =
						availableDows.includes(6) && availableDows.includes(0)
					if (rollsOver) {
						let startDow = 6
						let endDow = 0
						let foundStart = false
						let foundEnd = false
						for (var i = 0; i < 6; i++) {
							if (!availableDows.includes(startDow)) {
								startDow++
								foundStart = true
							}
							if (!availableDows.includes(endDow)) {
								endDow--
								foundEnd = true
							}
							if (foundStart && foundEnd) {
								break
							} else {
								startDow--
								endDow++
							}
						}
						openSchedule = menuSchedule[startDow]
						closeSchedule = menuSchedule[endDow]
					} else {
						let startDow = Math.min.apply(null, availableDows)
						let endDow = Math.max.apply(null, availableDows)
						openSchedule = menuSchedule[startDow]
						closeSchedule = menuSchedule[endDow]
					}
				}
				return {
					openSchedule: openSchedule,
					closeSchedule: closeSchedule
				}
			}
			return null
		}
	},
	methods: {
		open(options) {
			let isModalOpen = $(this.$refs.modal).is(":visible")
			if (isModalOpen) {
				return
			}
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Experiment Modal CTA Opened",
				data: {
					type: this.type
				}
			})
			this.type = options.type
			this.params = this.$route.query
			$(this.$refs.modal).modal("show")
		},
		clickCta() {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Experiment Modal CTA Clicked",
				data: {
					type: this.type
				}
			})
			switch (this.type) {
				case ExperimentType.REFERRAL_SMS: {
					eventBus.emit(EventBusEvents.OPEN_SHARE_MODAL, "sms")
					break
				}
				case ExperimentType.PREORDER: {
					const date = moment().format("YYYY-MM-DD")
					this.$refs.timeModal.open({
						date: date
					})
					break
				}
				case ExperimentType.ON_PREM: {
					this.$router.push({
						path: `/${this.$route.params.slug}/menu/event/${this.event.id}`
					})
					break
				}
				case ExperimentType.HAPPY_HOUR: {
					let availableDows = this.happyHourAvailableDays
					if (availableDows) {
						const todaysDow = momentUtils.todaysDow()
						let nextAvailableDow = todaysDow
						for (var i = 0; i < 6; i++) {
							if (availableDows.includes(nextAvailableDow)) {
								break
							} else {
								nextAvailableDow =
									nextAvailableDow > 6
										? (nextAvailableDow = 0)
										: (nextAvailableDow += 1)
							}
						}
						let happyHourDate = momentUtils
							.findDowFromToday(nextAvailableDow)
							.format("YYYY-MM-DD")
						this.$store.dispatch("setDeliveryDate", {
							date: happyHourDate,
							preserveCart: true
						})
					}
					break
				}
			}
			this.close()
		},
		close() {
			this.params = {}
			$(this.$refs.modal).modal("hide")
		}
	}
}
</script>
